import React, { useState, useEffect, Fragment } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import campaignService, { isCampaignActive } from '../../services/campaignService';
import campaignParticipantService from '../../services/campaignParticipantService';
import userService from '../../services/userService';
import { getProfilePlaceholder } from '../../utils/placeholders';
import { Dialog, Transition } from '@headlessui/react';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReviewModal from '../../components/common/ReviewModal';

const formatBudget = (budget) => {
  if (!budget) return '₱0';
  if (typeof budget === 'number') return `₱${budget.toLocaleString()}`;
  return `₱${budget.toString().replace(/[₱,]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return (value || 0).toString();
};

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [campaign, setCampaign] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicantsData, setApplicantsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewSubmissions, setReviewSubmissions] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [endingCampaign, setEndingCampaign] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [influencerStatusFilter, setInfluencerStatusFilter] = useState('all');
  const [budgetData, setBudgetData] = useState({ allocated: 0, spent: 0, pending: 0, remaining: 0, paidInfluencers: 0, pendingCount: 0 });
  const [budgetLoading, setBudgetLoading] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    // Fetch real campaign data from Firestore
    const fetchCampaignData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);

        
        // Get campaign from Firestore
        const campaignData = await campaignService.getCampaignById(id);

        
        if (campaignData) {
          // Verify this campaign belongs to the current brand user
          if (campaignData.brandId === currentUser?.uid) {
            setCampaign(campaignData);
          } else {
            console.error('Unauthorized access to campaign');
            setCampaign(null);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching campaign:', error);
        setLoading(false);
      }
    };
    
    fetchCampaignData();
  }, [id, currentUser]);

  // Fetch budget tracking data when campaign is loaded
  useEffect(() => {
    const fetchBudgetData = async () => {
      if (!campaign || !id) return;
      
      try {
        setBudgetLoading(true);
        
        // Query payments for this campaign
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('campaignId', '==', id)
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        
        let spent = 0;
        let pending = 0;
        let paidInfluencers = 0;
        let pendingCount = 0;
        
        paymentsSnap.forEach(doc => {
          const data = doc.data();
          const amount = parseFloat(data.amount) || 0;
          if (data.status === 'completed' || data.status === 'processing') {
            spent += amount;
            if (data.status === 'completed') paidInfluencers++;
          } else if (data.status === 'pending') {
            pending += amount;
            pendingCount++;
          }
        });
        
        const allocated = parseFloat(campaign.budget) || campaign.payment?.totalBudget || 0;
        const remaining = allocated - spent - pending;
        
        setBudgetData({ allocated, spent, pending, remaining, paidInfluencers, pendingCount });
      } catch (error) {
        console.error('Error fetching budget data:', error);
      } finally {
        setBudgetLoading(false);
      }
    };
    
    fetchBudgetData();
  }, [campaign, id]);

  // Fetch applications when campaign is loaded or tab changes to influencers
  useEffect(() => {
    const fetchApplications = async () => {
      if (!campaign || activeTab !== 'influencers') return;
      
      try {
        setLoadingApplications(true);

        
        // Get all participants/applications for this campaign
        const participantsData = await campaignParticipantService.getParticipantsByCampaignId(campaign.id);

        setApplications(participantsData);
        
        // Fetch influencer data for each application
        const influencersData = {};
        for (const application of participantsData) {
          if (!influencersData[application.influencerId]) {
            const influencer = await userService.getUserById(application.influencerId);
            if (influencer) {
              influencersData[application.influencerId] = influencer;
            }
          }
        }
        
        setApplicantsData(influencersData);
        setLoadingApplications(false);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setLoadingApplications(false);
      }
    };
    
    fetchApplications();
  }, [campaign, activeTab]);

  useEffect(() => {
    if (currentUser && campaign) {
      fetchReviewSubmissions();
    }
  }, [currentUser, campaign]);

  // Fetch review submissions for this campaign
  const fetchReviewSubmissions = async () => {
    if (!currentUser || !campaign) return;
    
    try {
      setLoadingReviews(true);

      
      // Query reviews collection for submissions where campaignId matches current campaign
      const reviewsRef = collection(db, 'reviews');
      const q = query(reviewsRef, where('campaignId', '==', id));
      
      const querySnapshot = await getDocs(q);

      
      const submissionsData = [];
      
      // Process each review document
      for (const docSnapshot of querySnapshot.docs) {
        const reviewData = docSnapshot.data();
        
        // Get influencer details if needed
        let influencerName = reviewData.influencerName || 'Unknown Influencer';
        let influencerAvatar = reviewData.influencerProfileImage || getProfilePlaceholder(reviewData.influencerName || 'User');
        
        if (reviewData.influencerId) {
          try {
            const influencerDoc = await getDoc(doc(db, 'users', reviewData.influencerId));
            if (influencerDoc.exists()) {
              const influencerData = influencerDoc.data();
              influencerName = influencerData.displayName || 
                (influencerData.firstName + ' ' + influencerData.lastName) || 
                influencerName;
              influencerAvatar = influencerData.profileImageUrl || influencerAvatar;
            }
          } catch (error) {
            console.error('Error fetching influencer details:', error);
          }
        }
        
        // Format the submission data
        submissionsData.push({
          id: docSnapshot.id,
          campaignId: reviewData.campaignId || '',
          campaignName: campaign.name || campaign.title || 'Unnamed Campaign',
          influencerId: reviewData.influencerId || '',
          influencerName: influencerName,
          influencerUsername: reviewData.influencerEmail || '',
          influencerAvatar: influencerAvatar,
          content: reviewData.content || reviewData.imageUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext fill="%236b7280" font-family="Arial" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Content%3C/text%3E%3C/svg%3E',
          contentType: reviewData.contentType || 'Review',
          caption: reviewData.caption || reviewData.text || '',
          status: reviewData.status || 'pending',
          submittedAt: reviewData.submittedAt || reviewData.createdAt || Timestamp.now(),
          feedback: reviewData.feedback || ''
        });
      }
      
      setReviewSubmissions(submissionsData);
    } catch (error) {
      console.error('Error fetching review submissions:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      
      // Format: Month Day, Year
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Get appropriate status badge
  const getReviewStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Need Revision
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  /**
   * Handle updating the status of an application
   * @param {string} applicationId - The ID of the application to update
   * @param {string} newStatus - The new status ('approved' or 'rejected')
   */
  const handleUpdateStatus = async (applicationId, newStatus) => {
    if (!applicationId || !newStatus || !campaign) return;
    
    try {
      // Update the application status in Firestore
      await campaignParticipantService.updateParticipantStatus(
        applicationId, 
        newStatus, 
        newStatus === 'rejected' ? 'Your application needs revision. Please check the feedback and consider reapplying.' : ''
      );
      

      
      // Update the local state
      setApplications(applications.map(app => {
        if (app.id === applicationId) {
          return { ...app, status: newStatus, statusUpdatedAt: new Date() };
        }
        return app;
      }));
      
    } catch (error) {
      console.error(`Error updating application status:`, error);
      showError(`Failed to ${newStatus} application.`, 'Please check your connection and try again.', 6000);
    }
  };

  /**
   * Handle viewing an influencer's profile
   * @param {string} influencerId - The ID of the influencer to view
   */
  const handleViewInfluencerProfile = (influencerId) => {
    if (!influencerId) {
      console.error('No influencer ID provided');
      return;
    }
    navigate(`/brand/influencers/${influencerId}`);
  };

  /**
   * Handle viewing application details
   * @param {Object} application - The application to view
   */
  const handleViewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setIsModalOpen(true);
  };

  const handleReviewInfluencer = (application) => {
    const influencer = applicantsData[application.influencerId] || {};
    setReviewTarget({
      participantId: application.id,
      revieweeId: application.influencerId,
      revieweeName: influencer.displayName || 'Influencer',
    });
    setShowReviewModal(true);
  };

  /**
   * Handle ending an active campaign
   * Marks the campaign as completed, stops accepting new applications,
   * and notifies participating influencers.
   */
  const handleEndCampaign = async () => {
    if (!campaign || !id) return;

    const confirmed = window.confirm(
      'Are you sure you want to end this campaign? This will mark the campaign as completed, stop accepting new applications, and notify participating influencers.'
    );

    if (!confirmed) return;

    setEndingCampaign(true);
    try {
      await campaignService.updateCampaign(id, {
        status: 'completed',
        endedAt: new Date(),
        endDate: new Date()
      });

      showSuccess(
        'Campaign Ended',
        'The campaign has been marked as completed. Participating influencers will be notified.',
        5000
      );

      // Refresh campaign data
      const updatedCampaign = await campaignService.getCampaignById(id);
      if (updatedCampaign) {
        setCampaign(updatedCampaign);
      }
    } catch (error) {
      console.error('Error ending campaign:', error);
      showError(
        'Failed to end campaign.',
        'Please check your connection and try again.',
        6000
      );
    } finally {
      setEndingCampaign(false);
    }
  };

  const getStatusBadgeClass = (status, campaignObj) => {
    // For active campaigns, check if they're truly active based on end date
    if (status === 'active') {
      return isCampaignActive(campaignObj)
        ? 'bg-green-100 text-green-800'  // Truly active
        : 'bg-red-100 text-red-800';     // End date has passed
    }
    
    // For other statuses
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'invited':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'not_submitted':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Helper function to determine if campaign can be edited
  const canEditCampaign = (campaignObj) => {
    // Only allow editing if campaign is active or draft
    if (campaignObj.status === 'draft') return true;
    if (campaignObj.status === 'active') {
      // For active campaigns, only allow editing if end date hasn't passed
      return isCampaignActive(campaignObj);
    }
    return false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-8 text-center">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Campaign Not Found</h3>
        <p className="text-sm text-gray-600 mb-5">The campaign you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/brand/campaigns"
          className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
        >
          Back to Campaigns
        </Link>
      </div>
    );
  }

  return (
    <div>

      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', to: '/brand/campaigns' },
          { label: campaign.name },
        ]}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-semibold text-gray-800">{campaign.name}</h2>
        </div>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <div className="flex items-center mb-3 md:mb-0">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(campaign.status, campaign)}`}>
              {campaign.status === 'active' && !isCampaignActive(campaign)
                ? 'Ended'
                : campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
            <span className="mx-2 text-gray-300">•</span>
            <span className="text-sm text-gray-600">
              {formatDate(campaign.startDate)} to {formatDate(campaign.endDate)}
            </span>
          </div>
          
          <div className="flex space-x-2">
            <Link 
              to={`/brand/campaigns/${id}/edit`} 
              className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-primary transition-colors duration-200"
            >
              Edit Campaign
            </Link>
            {isCampaignActive(campaign) && campaign.status === 'active' && (
              <button
                type="button"
                onClick={handleEndCampaign}
                disabled={endingCampaign}
                className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {endingCampaign ? 'Ending...' : 'End Campaign'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-5 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 px-1">
            <button
              className={`whitespace-nowrap py-2.5 px-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'overview'
                  ? 'border-primary text-primary bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`whitespace-nowrap py-2.5 px-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'influencers'
                  ? 'border-primary text-primary bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('influencers')}
            >
              Influencers
            </button>
            <button
              className={`whitespace-nowrap py-2.5 px-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'content'
                  ? 'border-primary text-primary bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('content')}
            >
              Content
            </button>
            <button
              className={`whitespace-nowrap py-2.5 px-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'analytics'
                  ? 'border-primary text-primary bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
          </nav>
        </div>

        <div className="p-6 bg-white">
          {activeTab === 'overview' && (
            <div>
              {/* Campaign Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                  <p className="text-sm font-medium text-gray-500">Influencers</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{campaign.filledSpots || 0}</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                  <p className="text-sm font-medium text-gray-500">Budget</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{formatBudget(campaign.budget)}</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                  <p className="text-sm font-medium text-gray-500">Duration</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {(() => {
                      const start = campaign.startDate?.toDate ? campaign.startDate.toDate() : new Date(campaign.startDate);
                      const end = campaign.endDate?.toDate ? campaign.endDate.toDate() : new Date(campaign.endDate);
                      const diffTime = Math.abs(end - start);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return `${diffDays} days`;
                    })()}
                  </p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                  <p className="text-sm font-medium text-gray-500">Submissions</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {campaign.analytics?.submittedContent || 0}
                  </p>
                </div>
              </div>
              
              {/* 2-COLUMN LAYOUT */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ===== LEFT COLUMN (main content) ===== */}
                <div className="lg:col-span-2 space-y-6">
              
              {/* Campaign Image */}
              {campaign.imageUrl && (
                <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-900">
                      Campaign Image
                    </h3>
                  </div>
                  <div className="p-6">
                    <img 
                      src={campaign.imageUrl} 
                      alt={campaign.name} 
                      className="w-full h-64 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Campaign Details */}
              <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-900">
                    Campaign Details
                  </h3>
                </div>
                <div className="px-6 py-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Description
                      </h4>
                      <p className="text-sm text-gray-800 bg-gray-50 p-4 rounded-md border border-gray-100">{campaign.description}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Objectives
                      </h4>
                      <div className="flex flex-wrap bg-gray-50 p-4 rounded-md border border-gray-100">
                        {(campaign.objectives || []).map((objective, index) => (
                          <span key={index} className="mr-2 mb-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {objective}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Category
                      </h4>
                      <p className="text-sm text-gray-800 bg-gray-50 p-4 rounded-md border border-gray-100">{campaign.category}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Content Type
                      </h4>
                      <div className="flex flex-wrap bg-gray-50 p-4 rounded-md border border-gray-100">
                        {(campaign.contentType || []).map((type, index) => (
                          <span key={index} className="mr-2 mb-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Compensation
                      </h4>
                      <div className="flex items-center bg-gray-50 p-4 rounded-md border border-gray-100">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${campaign.compensationType === 'monetary' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-purple-50 text-purple-700 border border-purple-100'}`}>
                          {campaign.compensationType === 'monetary' ? 'Monetary' : 'Product'}
                        </span>
                        <span className="ml-2 text-sm text-gray-800 font-medium">
                          {campaign.compensationType === 'monetary' && campaign.compensationAmount && !isNaN(campaign.compensationAmount)
                            ? `₱${parseInt(campaign.compensationAmount).toLocaleString()}`
                            : campaign.compensationAmount || 'Not specified'}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Minimum Followers
                      </h4>
                      <div className="bg-gray-50 p-4 rounded-md border border-gray-100 flex items-center">
                        <span className="text-sm text-gray-800 font-medium">{(campaign.minFollowers || 0).toLocaleString()}</span>
                        <span className="ml-2 text-xs text-gray-500">followers required</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 bg-white border border-gray-100 rounded-lg shadow-sm p-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">
                      Content Guidelines
                    </h4>
                    <div className="bg-gray-50 p-4 rounded-md border border-gray-100 text-sm text-gray-800">
                      {(() => {
                        const content = campaign.contentGuidelines || 'No content guidelines provided.';
                        
                        // Simple formatting function that preserves original numbering and spacing
                        const formatContent = (text) => {
                          // Split content into lines and preserve ALL lines including empty ones
                          const lines = text.split(/\r?\n/);
                          const formattedElements = [];
                          
                          lines.forEach((line, index) => {
                            const trimmedLine = line.trim();
                            
                            // Handle empty lines to preserve spacing from pasted content
                            if (!trimmedLine) {
                              formattedElements.push(
                                <div key={`empty-${index}`} className="h-4"></div>
                              );
                              return;
                            }
                            
                            // Check for numbered items - nested first, then simple
                            const nestedNumberMatch = trimmedLine.match(/^(\d+\.\d+)\s+(.*)/);  // Matches "4.1 Text"
                            const simpleNumberMatch = trimmedLine.match(/^(\d+)\.\s*(.*)/);     // Matches "4. Text"
                            
                            if (nestedNumberMatch) {
                              // Handle nested numbering like 2.1., 2.2., etc.
                              const fullNumber = nestedNumberMatch[1];
                              const content = nestedNumberMatch[2].trim();
                              formattedElements.push(
                                <div key={`nested-${index}`} className="mb-2 ml-6 font-bold">
                                  <span className="font-bold text-gray-900">
                                    {fullNumber}
                                  </span>
                                  <span className="text-gray-700 ml-1">
                                    {content}
                                  </span>
                                </div>
                              );
                            }
                            else if (simpleNumberMatch) {
                              // Handle simple numbering like 1., 2., 3., etc.
                              const number = simpleNumberMatch[1];
                              const content = simpleNumberMatch[2].trim();
                              formattedElements.push(
                                <div key={`simple-${index}`} className="mb-2 font-bold">
                                  <span className="font-bold text-gray-900">
                                    {number}.
                                  </span>
                                  <span className="text-gray-700 ml-1">
                                    {content}
                                  </span>
                                </div>
                              );
                            }
                            // Check for checkmark items (✅)
                            else if (trimmedLine.includes('✅')) {
                              const cleanText = trimmedLine.replace(/✅\s*/, '').trim();
                              formattedElements.push(
                                <div key={`check-${index}`} className="flex items-start space-x-2 mb-2">
                                  <span className="text-green-500 mt-0.5">✅</span>
                                  <span className="text-gray-700">{cleanText}</span>
                                </div>
                              );
                            }
                            // Check for X mark items (❌)
                            else if (trimmedLine.includes('❌')) {
                              const cleanText = trimmedLine.replace(/❌\s*/, '').trim();
                              formattedElements.push(
                                <div key={`x-${index}`} className="flex items-start space-x-2 mb-2">
                                  <span className="text-red-500 mt-0.5">❌</span>
                                  <span className="text-gray-700">{cleanText}</span>
                                </div>
                              );
                            }
                            // Check for indented items (starting with spaces/tabs)
                            else if (line.match(/^\s+/)) {
                              formattedElements.push(
                                <div key={`indented-${index}`} className="ml-6 mb-1 text-gray-700">
                                  {trimmedLine}
                                </div>
                              );
                            }
                            // Regular text lines
                            else {
                              formattedElements.push(
                                <div key={`text-${index}`} className="mb-2 text-gray-700">
                                  {trimmedLine}
                                </div>
                              );
                            }
                          });
                          
                          return formattedElements.length > 0 ? formattedElements : (
                            <p className="text-gray-700">{text}</p>
                          );
                        };
                        
                        return formatContent(content);
                      })()} 
                    </div>
                  </div>
                  
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                    <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        Required Hashtags
                      </h4>
                      <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm text-gray-800">
                        {campaign.requiredHashtags}
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        Required Mentions
                      </h4>
                      <div className="bg-purple-50 p-4 rounded-md border border-purple-100 text-sm text-gray-800">
                        {campaign.requiredMentions}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                </div>

                {/* ===== RIGHT COLUMN (sidebar) ===== */}
                <div className="lg:col-span-1 space-y-6 lg:self-start lg:sticky lg:top-6">
              {/* Budget Tracking Card */}
              <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-900">
                    Budget Tracking
                  </h3>
                </div>
                <div className="p-6">
                  {budgetLoading ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Loading budget data...</span>
                    </div>
                  ) : (
                    <>
                      {/* Summary (stacked for sidebar) */}
                      <div className="space-y-3 mb-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Allocated</p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">
                            ₱{budgetData.allocated.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <p className="text-xs font-medium text-orange-600 uppercase tracking-wider">Spent (Paid + Processing)</p>
                          <p className="text-2xl font-bold text-orange-900 mt-1">
                            ₱{budgetData.spent.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div className={`border rounded-lg p-4 ${budgetData.remaining < 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <p className={`text-xs font-medium uppercase tracking-wider ${budgetData.remaining < 0 ? 'text-red-600' : 'text-green-600'}`}>Remaining</p>
                          <p className={`text-2xl font-bold mt-1 ${budgetData.remaining < 0 ? 'text-red-900' : 'text-green-900'}`}>
                            ₱{budgetData.remaining.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      {budgetData.allocated > 0 && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                            <span>Budget Utilization</span>
                            <span>{Math.round(((budgetData.spent + budgetData.pending) / budgetData.allocated) * 100)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden flex">
                            {budgetData.spent > 0 && (
                              <div
                                className="bg-orange-500 h-3 transition-all duration-500"
                                style={{ width: `${Math.min((budgetData.spent / budgetData.allocated) * 100, 100)}%` }}
                                title={`Spent: ₱${budgetData.spent.toLocaleString()}`}
                              />
                            )}
                            {budgetData.pending > 0 && (
                              <div
                                className="bg-yellow-400 h-3 transition-all duration-500"
                                style={{ width: `${Math.min((budgetData.pending / budgetData.allocated) * 100, 100 - (budgetData.spent / budgetData.allocated) * 100)}%` }}
                                title={`Pending: ₱${budgetData.pending.toLocaleString()}`}
                              />
                            )}
                          </div>
                          <div className="flex gap-4 mt-2 text-xs">
                            <span className="flex items-center"><span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-1.5"></span>Spent</span>
                            <span className="flex items-center"><span className="inline-block w-3 h-3 rounded-full bg-yellow-400 mr-1.5"></span>Pending</span>
                            <span className="flex items-center"><span className="inline-block w-3 h-3 rounded-full bg-gray-200 mr-1.5"></span>Remaining</span>
                          </div>
                        </div>
                      )}

                      {/* Breakdown row */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span><strong className="text-gray-900">{budgetData.paidInfluencers}</strong> paid</span>
                        <span>·</span>
                        <span><strong className="text-gray-900">{budgetData.pendingCount}</strong> pending</span>
                        {campaign.compensationAmount && (
                          <>
                            <span>·</span>
                            <span>Per: <strong className="text-gray-900">₱{parseFloat(campaign.compensationAmount).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong></span>
                          </>
                        )}
                      </div>

                      {/* Warning banners */}
                      {budgetData.remaining < 0 && (
                        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-700">
                            Budget exceeded by ₱{Math.abs(budgetData.remaining).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}. 
                            Consider increasing the campaign budget or reducing the number of influencers.
                          </p>
                        </div>
                      )}
                      {budgetData.remaining >= 0 && budgetData.remaining < budgetData.pending && budgetData.pending > 0 && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-sm text-yellow-700">
            Insufficient budget for pending payments. You need ₱{(budgetData.pending - budgetData.remaining).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} more to cover all pending payments.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'content' && (
            <div className="p-6 bg-white">
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Content Submissions
                </h3>
                <p className="text-sm text-gray-500">View and manage content submissions for this campaign.</p>
              </div>
              
              {loadingReviews ? (
                <div className="flex flex-col justify-center items-center h-40 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <LoadingSpinner size="xl" color="primary" />
            <p className="text-sm text-gray-600 font-medium">Loading content submissions...</p>
                </div>
              ) : reviewSubmissions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviewSubmissions.map((submission) => (
                    <div key={submission.id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                      <div className="relative">
                        {submission.contentUrls && submission.contentUrls.length > 0 && (
                          <img 
                            src={submission.contentUrls[0]} 
                            alt="Content preview" 
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200"%3E%3Crect fill="%23f3f4f6" width="300" height="200"/%3E%3Ctext fill="%236b7280" font-family="Arial" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Preview%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        )}
                        <div className="absolute top-2 right-2">
                          {getReviewStatusBadge(submission.status)}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center mb-3">
                          <img 
                            src={submission.influencerAvatar || getProfilePlaceholder(submission.influencerName || 'User')} 
                            alt={submission.influencerName} 
                            className="w-8 h-8 rounded-full mr-2 object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = getProfilePlaceholder(submission.influencerName || 'User');
                            }}
                          />
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">{submission.influencerName}</h3>
                            <p className="text-xs text-gray-500">
                              {formatDate(submission.submittedAt)}
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-700 line-clamp-2 mb-3">{submission.caption}</p>
                        
                        {submission.status === 'rejected' && submission.feedback && (
                          <div className="bg-red-50 border border-red-100 rounded p-2 mb-3">
                            <p className="text-xs text-red-800">
                              <span className="font-medium">Feedback:</span> {submission.feedback}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex justify-end">
                          <Link
                            to={`/brand/content-approval/${submission.id}`}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-10 text-center">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">No content submissions yet</h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto">
                    When influencers submit their final content for review, it will appear here.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'analytics' && (
            <div>
              {/* Analytics Summary Stat Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Reach</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatNumber(campaign?.analytics?.totalReach || 0)}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Engagement</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatNumber(campaign?.analytics?.totalEngagement || 0)}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking Link Clicks</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatNumber(campaign?.trackingAnalytics?.totalClicks || 0)}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Content Submissions</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatNumber(campaign?.analytics?.submittedContent || 0)}</p>
                </div>
              </div>

              {/* Campaign Performance Section */}
              <div className="px-6 pb-6">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-900">
                      Campaign Performance
                    </h3>
                  </div>
                  <div className="px-6 py-5 space-y-6">
                    {/* Applications received vs approved */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Applications Received vs Approved</h4>
                        <span className="text-sm text-gray-500">
                          {campaign?.analytics?.approvedApplications || 0} / {campaign?.analytics?.applications || 0}
                        </span>
                      </div>
                      {(() => {
                        const total = campaign?.analytics?.applications || 0;
                        const approved = campaign?.analytics?.approvedApplications || 0;
                        const percentage = total > 0 ? Math.round((approved / total) * 100) : 0;
                        return (
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-primary h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-1.5">
                        {(() => {
                          const total = campaign?.analytics?.applications || 0;
                          const approved = campaign?.analytics?.approvedApplications || 0;
                          return total > 0 ? `${Math.round((approved / total) * 100)}% approval rate` : 'No applications yet';
                        })()}
                      </p>
                    </div>

                    {/* Content submitted vs approved */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Content Submitted vs Approved</h4>
                        <span className="text-sm text-gray-500">
                          {campaign?.analytics?.approvedContent || 0} / {campaign?.analytics?.submittedContent || 0}
                        </span>
                      </div>
                      {(() => {
                        const total = campaign?.analytics?.submittedContent || 0;
                        const approved = campaign?.analytics?.approvedContent || 0;
                        const percentage = total > 0 ? Math.round((approved / total) * 100) : 0;
                        return (
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-green-500 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-1.5">
                        {(() => {
                          const total = campaign?.analytics?.submittedContent || 0;
                          const approved = campaign?.analytics?.approvedContent || 0;
                          return total > 0 ? `${Math.round((approved / total) * 100)}% approval rate` : 'No content submitted yet';
                        })()}
                      </p>
                    </div>

                    {/* Influencer engagement rate */}
                    {campaign?.analytics?.engagementRate != null && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Influencer Engagement Rate</h4>
                          <span className="text-sm font-semibold text-gray-900">
                            {typeof campaign.analytics.engagementRate === 'number'
                              ? `${campaign.analytics.engagementRate.toFixed(2)}%`
                              : campaign.analytics.engagementRate}
                          </span>
                        </div>
                        {(() => {
                          const rate = typeof campaign.analytics.engagementRate === 'number'
                            ? campaign.analytics.engagementRate
                            : parseFloat(campaign.analytics.engagementRate) || 0;
                          return (
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div
                                className="bg-purple-500 h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(rate, 100)}%` }}
                              ></div>
                            </div>
                          );
                        })()}
                        <p className="text-xs text-gray-500 mt-1.5">Average engagement across all influencers</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'influencers' && (
            <div className="p-6 bg-white">
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Campaign Applications
                </h3>
                <p className="text-sm text-gray-500">Manage influencer applications for this campaign.</p>
              </div>
              
              {loadingApplications ? (
                <div className="flex justify-center items-center h-40">
                  <LoadingSpinner size="lg" color="primary" />
                </div>
              ) : applications.length > 0 ? (
                <>
                  {/* Search and Filter Controls */}
                  <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
                      <div className="flex-1 max-w-sm">
                        <input
                          type="text"
                          value={influencerSearch}
                          onChange={(e) => { setInfluencerSearch(e.target.value); setCurrentPage(1); }}
                          placeholder="Search by name or email..."
                          className="pr-3 py-2 w-full border border-gray-300 rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <select
                        value={influencerStatusFilter}
                        onChange={(e) => { setInfluencerStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <button
                        onClick={() => { setInfluencerSearch(''); setInfluencerStatusFilter('all'); setCurrentPage(1); }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 transition-colors duration-150"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Results count */}
                  <div className="mb-3 text-sm text-gray-600">
                    {(() => {
                      const filteredApplications = applications.filter(application => {
                        const influencer = applicantsData[application.influencerId] || {};
                        const matchesSearch = !influencerSearch || 
                          (influencer.displayName || '').toLowerCase().includes(influencerSearch.toLowerCase()) ||
                          (influencer.email || '').toLowerCase().includes(influencerSearch.toLowerCase());
                        const matchesStatus = influencerStatusFilter === 'all' || application.status === influencerStatusFilter;
                        return matchesSearch && matchesStatus;
                      });
                      return (
                        <p>
                          Showing <span className="font-medium text-gray-900">{filteredApplications.length}</span> of{' '}
                          <span className="font-medium text-gray-900">{applications.length}</span> influencer{filteredApplications.length !== 1 ? 's' : ''}
                          {influencerSearch && ` matching "${influencerSearch}"`}
                          {influencerStatusFilter !== 'all' && ` with status "${influencerStatusFilter}"`}
                        </p>
                      );
                    })()}
                  </div>

                  <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-100">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied On</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submission</th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          const filteredApplications = applications.filter(application => {
                            const influencer = applicantsData[application.influencerId] || {};
                            const matchesSearch = !influencerSearch || 
                              (influencer.displayName || '').toLowerCase().includes(influencerSearch.toLowerCase()) ||
                              (influencer.email || '').toLowerCase().includes(influencerSearch.toLowerCase());
                            const matchesStatus = influencerStatusFilter === 'all' || application.status === influencerStatusFilter;
                            return matchesSearch && matchesStatus;
                          });
                          const startIndex = (currentPage - 1) * itemsPerPage;
                          const endIndex = startIndex + itemsPerPage;
                          const paginatedApplications = filteredApplications.slice(startIndex, endIndex);
                          if (paginatedApplications.length === 0) {
                            return (
                              <tr>
                                <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500">
                                  No influencers match your search or filter.
                                </td>
                              </tr>
                            );
                          }
                          return paginatedApplications.map((application) => {
                        const influencer = applicantsData[application.influencerId] || {};
                        return (
                          <tr key={application.id} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 relative">
                                  <img 
                                    className="h-10 w-10 rounded-full object-cover cursor-pointer border border-gray-200 shadow-sm" 
                                    src={influencer.profileImageUrl || influencer.profileImage || '/assets/default-avatar.png'} 
                                    alt={influencer.displayName || 'Influencer'}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '/assets/default-avatar.png';
                                    }}
                                    onClick={() => handleViewInfluencerProfile(influencer.uid)}
                                  />
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary transition-colors duration-150"
                                       onClick={() => handleViewInfluencerProfile(influencer.uid)}>
                                    {influencer.displayName || 'Influencer'}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {influencer.email || ''}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusBadgeClass(application.status)} border`}>
                                <span className="flex items-center">
                                  <span className="h-1.5 w-1.5 rounded-full mr-1.5 bg-current opacity-70"></span>
                                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                                </span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {application.appliedAt?.toDate ? application.appliedAt.toDate().toLocaleDateString() : 'Unknown'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="group relative">
                                <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full border ${getStatusBadgeClass(application.submissionStatus)}`}>
                                  <span className="flex items-center">
                                    <span className="h-1.5 w-1.5 rounded-full mr-1.5 bg-current opacity-70"></span>
                                    {application.submissionStatus === 'not_submitted' ? 'Not Submitted' : 
                                     application.submissionStatus.charAt(0).toUpperCase() + application.submissionStatus.slice(1)}
                                  </span>
                                </span>
                                <div className="hidden group-hover:block absolute z-10 p-2.5 bg-gray-800 text-white text-xs rounded-md shadow-lg w-56 -ml-24 mt-2">
                                  <div className="font-medium mb-1 text-gray-100">
                                    {application.submissionStatus === 'not_submitted' ? 'Not Submitted' : 
                                     application.submissionStatus.charAt(0).toUpperCase() + application.submissionStatus.slice(1)}
                                  </div>
                                  <p className="text-gray-300 text-xs">
                                    {application.submissionStatus === 'not_submitted' ? 
                                      'The influencer has applied but hasn\'t submitted content yet. They will submit content after approval.' : 
                                      application.submissionStatus === 'submitted' ? 
                                        'The influencer has submitted content for review.' : 
                                        application.submissionStatus === 'approved' ? 
                                          'You have approved the influencer\'s content submission.' : 
                                          'You have requested revision for the influencer\'s content submission.'}
                                  </p>
                                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 w-2.5 h-2.5 bg-gray-800 rotate-45"></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex space-x-2 justify-end">
                                <button
                                  onClick={() => handleViewApplicationDetails(application)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1 transition-colors duration-150"
                                >
                                  View
                                </button>
                                {application.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleUpdateStatus(application.id, 'approved')}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 focus:ring-offset-1 transition-colors duration-150"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleUpdateStatus(application.id, 'rejected')}
                                      className="inline-flex items-center px-2 py-1 border border-red-300 text-xs font-medium rounded shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-500 focus:ring-offset-1 transition-colors duration-150"
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {application.status === 'approved' && campaign?.status === 'completed' && (
                                  <button
                                    onClick={() => handleReviewInfluencer(application)}
                                    className="inline-flex items-center px-2 py-1 border border-amber-300 text-xs font-medium rounded shadow-sm text-amber-700 bg-white hover:bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:ring-offset-1 transition-colors duration-150"
                                  >
                                    Review
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {(() => {
                    const filteredApplications = applications.filter(application => {
                      const influencer = applicantsData[application.influencerId] || {};
                      const matchesSearch = !influencerSearch || 
                        (influencer.displayName || '').toLowerCase().includes(influencerSearch.toLowerCase()) ||
                        (influencer.email || '').toLowerCase().includes(influencerSearch.toLowerCase());
                      const matchesStatus = influencerStatusFilter === 'all' || application.status === influencerStatusFilter;
                      return matchesSearch && matchesStatus;
                    });
                    return Math.ceil(filteredApplications.length / itemsPerPage) > 1 ? (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredApplications.length / itemsPerPage)))}
                          disabled={currentPage === Math.ceil(filteredApplications.length / itemsPerPage)}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredApplications.length)}</span> of{' '}
                            <span className="font-medium">{filteredApplications.length}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Previous</span>
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                            {[...Array(Math.ceil(filteredApplications.length / itemsPerPage))].map((_, index) => {
                              const pageNumber = index + 1;
                              const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
                              if (
                                pageNumber === 1 ||
                                pageNumber === totalPages ||
                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                              ) {
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setCurrentPage(pageNumber)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                      currentPage === pageNumber
                                        ? 'z-10 bg-primary border-primary text-white'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                );
                              } else if (
                                pageNumber === currentPage - 2 ||
                                pageNumber === currentPage + 2
                              ) {
                                return <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                              }
                              return null;
                            })}
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredApplications.length / itemsPerPage)))}
                              disabled={currentPage === Math.ceil(filteredApplications.length / itemsPerPage)}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Next</span>
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                    ) : null;
                  })()}
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-10 text-center">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No applications yet</h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto">When influencers apply to your campaign, they will appear here. Share your campaign to attract more influencers.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Application Details Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {selectedApplication && (
                    <>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Application Details
                      </Dialog.Title>
                      <div className="mt-4">
                        <div className="flex items-center mb-4">
                          <div className="flex-shrink-0 h-12 w-12">
                            {applicantsData[selectedApplication.influencerId] && (
                              <img 
                                className="h-12 w-12 rounded-full object-cover" 
                                src={applicantsData[selectedApplication.influencerId].profileImageUrl || 
                                     applicantsData[selectedApplication.influencerId].profileImage || 
                                     '/assets/default-avatar.png'} 
                                alt={applicantsData[selectedApplication.influencerId].displayName || 'Influencer'}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/default-avatar.png';
                                }}
                              />
                            )}
                          </div>
                          <div className="ml-4">
                            <h4 className="text-lg font-medium text-gray-900">
                              {applicantsData[selectedApplication.influencerId]?.displayName || 'Influencer'}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {applicantsData[selectedApplication.influencerId]?.email || ''}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Status</h5>
                            <p className="text-gray-900">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedApplication.status)}`}>
                                {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                              </span>
                            </p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Applied On</h5>
                            <p className="text-gray-900">
                              {selectedApplication.appliedAt?.toDate ? selectedApplication.appliedAt.toDate().toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <h5 className="text-sm font-medium text-gray-500">Application Message</h5>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded">
                            {selectedApplication.message || 'No message provided'}
                          </p>
                        </div>
                        
                        <div className="mb-4">
                          <h5 className="text-sm font-medium text-gray-500">Why are you a good fit for this campaign?</h5>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded">
                            {selectedApplication.note || 'No additional information provided'}
                          </p>
                        </div>
                        
                        <div className="mb-4">
                          <h5 className="text-sm font-medium text-gray-500">Submission Status</h5>
                          <div className="flex items-center">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedApplication.submissionStatus)}`}>
                              {selectedApplication.submissionStatus === 'not_submitted' ? 'Not Submitted' : 
                               selectedApplication.submissionStatus.charAt(0).toUpperCase() + selectedApplication.submissionStatus.slice(1)}
                            </span>
                            {selectedApplication.status === 'approved' && selectedApplication.submissionStatus === 'not_submitted' && (
                              <span className="text-xs text-gray-500 ml-2 italic">Waiting for influencer to submit content</span>
                            )}
                          </div>
                        </div>
                        
                        {selectedApplication.submissionStatus === 'submitted' && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-gray-500">Content Submission</h5>
                            <div className="bg-gray-50 p-3 rounded">
                              <Link
                                to={`/brand/content-approval/${selectedApplication.id}`}
                                className="text-primary hover:text-primary-dark text-sm font-medium"
                              >
                                View content submission
                              </Link>
                            </div>
                          </div>
                        )}
                        
                        {selectedApplication.status === 'pending' && (
                          <div className="flex space-x-2 mt-4">
                            <button 
                              onClick={() => {
                                handleUpdateStatus(selectedApplication.id, 'approved');
                                setIsModalOpen(false);
                              }}
                              className="inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
                            >
                              Approve Application
                            </button>
                            <button
                              onClick={() => {
                                handleUpdateStatus(selectedApplication.id, 'rejected');
                                setIsModalOpen(false);
                              }}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Reject Application
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="mt-6 flex justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={() => handleViewInfluencerProfile(selectedApplication.influencerId)}
                        >
                          View Influencer Profile
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={() => setIsModalOpen(false)}
                        >
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Review Modal */}
      {showReviewModal && reviewTarget && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => { setShowReviewModal(false); setReviewTarget(null); }}
          campaignId={id}
          participantId={reviewTarget.participantId}
          revieweeId={reviewTarget.revieweeId}
          revieweeType="influencer"
          revieweeName={reviewTarget.revieweeName}
          reviewerType="brand"
        />
      )}
    </div>
  );
};

export default CampaignDetail;
